import { findPolicyDetailById } from '../mock-data/policies.mock';
import {
  MOCK_USER_DETAILS,
  findUserDetailById,
  getMockUsers,
} from '../mock-data/users.mock';
import {
  createMockResponse,
  createMockErrorResponse,
} from '../mock.interceptor';

import type {
  AssignPolicyApiRequest,
  CreateUserApiRequest,
  UpdateUserApiRequest,
  UserBaseApiResponse,
  UserDetailApiResponse,
  UserListApiResponse,
  UserPoliciesApiResponse,
} from '../../features/users/users.models';
import type { HttpResponse } from '@angular/common/http';
import type { Observable } from 'rxjs';

/**
 * /users* へのリクエストをハンドリング
 * path はテナントプレフィックス除去済みの短縮パス（例: /users, /users/:id）
 */
export function handleUsersRequest(
  method: string,
  path: string,
  body: unknown,
): Observable<HttpResponse<unknown>> | null {
  // GET /users - ユーザー一覧取得
  if (method === 'GET' && path === '/users') {
    return handleGetUsers();
  }

  // POST /users - ユーザー招待
  if (method === 'POST' && path === '/users') {
    return handleCreateUser(body as CreateUserApiRequest);
  }

  // POST /users/:id/policies - ポリシー割当
  const assignPolicyMatch = /^\/users\/([^/]+)\/policies$/.exec(path);
  if (method === 'POST' && assignPolicyMatch) {
    const userId = assignPolicyMatch[1];
    return handleAssignPolicy(userId, body as AssignPolicyApiRequest);
  }

  // DELETE /users/:id/policies/:policyId - ポリシー解除
  const unassignPolicyMatch = /^\/users\/([^/]+)\/policies\/([^/]+)$/.exec(
    path,
  );
  if (method === 'DELETE' && unassignPolicyMatch) {
    const userId = unassignPolicyMatch[1];
    const policyId = unassignPolicyMatch[2];
    return handleUnassignPolicy(userId, policyId);
  }

  // GET /users/:id - ユーザー詳細取得
  const getUserDetailMatch = /^\/users\/([^/]+)$/.exec(path);
  if (method === 'GET' && getUserDetailMatch) {
    const userId = getUserDetailMatch[1];
    return handleGetUserDetail(userId);
  }

  // PATCH /users/:id - ユーザー更新
  const patchUserMatch = /^\/users\/([^/]+)$/.exec(path);
  if (method === 'PATCH' && patchUserMatch) {
    const userId = patchUserMatch[1];
    return handleUpdateUser(userId, body as UpdateUserApiRequest);
  }

  // DELETE /users/:id - ユーザー削除
  const deleteUserMatch = /^\/users\/([^/]+)$/.exec(path);
  if (method === 'DELETE' && deleteUserMatch) {
    const userId = deleteUserMatch[1];
    return handleDeleteUser(userId);
  }

  return null;
}

/**
 * UserDetailApiResponse から API レスポンス用の UserBaseApiResponse を抽出
 */
function toUserBase(user: UserDetailApiResponse): UserBaseApiResponse {
  return {
    publicId: user.publicId,
    email: user.email,
    name: user.name,
    role: user.role,
    userType: user.userType,
    createdAt: user.createdAt,
    updatedAt: user.updatedAt,
  };
}

/**
 * GET /api/v0/tenants/:tenantId/users - ユーザー一覧取得
 * カーソルページネーション対応（モックでは全件返却）
 */
function handleGetUsers(): Observable<HttpResponse<UserListApiResponse>> {
  return createMockResponse({ items: getMockUsers(), nextCursor: null });
}

/**
 * POST /api/v0/tenants/:tenantId/users - ユーザー招待
 * 仕様: 作成直後はポリシー未割当のため role は必ず member。レスポンスは User（policies なし）。
 * 重複メールの場合は409を返す
 */
function handleCreateUser(
  body: CreateUserApiRequest,
): Observable<HttpResponse<UserBaseApiResponse | { message: string }>> {
  // 重複メールチェック
  const existing = MOCK_USER_DETAILS.find((u) => u.email === body.email);
  if (existing) {
    return createMockErrorResponse(
      'このメールアドレスはすでに登録されています',
      409,
    );
  }

  const now = new Date().toISOString();
  const newUserDetail: UserDetailApiResponse = {
    publicId: `user-new-${Date.now()}`,
    email: body.email,
    name: body.name,
    role: 'member',
    userType: 'Customer',
    policies: [],
    createdAt: now,
    updatedAt: now,
  };
  MOCK_USER_DETAILS.push(newUserDetail);

  return createMockResponse<UserBaseApiResponse>(
    toUserBase(newUserDetail),
    201,
  );
}

/**
 * GET /api/v0/tenants/:tenantId/users/:id - ユーザー詳細取得
 */
function handleGetUserDetail(
  userId: string,
): Observable<HttpResponse<UserDetailApiResponse | { message: string }>> {
  const user = findUserDetailById(userId);
  if (!user) {
    return createMockErrorResponse('ユーザーが見つかりません', 404);
  }
  return createMockResponse<UserDetailApiResponse>(user);
}

/**
 * PATCH /api/v0/tenants/:tenantId/users/:id - ユーザー更新
 * 仕様: レスポンスは更新後の UserDetail（GET /users/:id と同じ内容）。
 * name は実 API と同じく前後の空白を除いて保存し、空になる場合は 400 を返す。
 */
function handleUpdateUser(
  userId: string,
  body: UpdateUserApiRequest,
): Observable<HttpResponse<UserDetailApiResponse | { message: string }>> {
  const user = findUserDetailById(userId);
  if (!user) {
    return createMockErrorResponse('ユーザーが見つかりません', 404);
  }

  const name = body.name.trim();
  if (name === '') {
    return createMockErrorResponse('ユーザー名を入力してください', 400);
  }

  user.name = name;
  user.updatedAt = new Date().toISOString();

  return createMockResponse<UserDetailApiResponse>(user);
}

/**
 * DELETE /api/v0/tenants/:tenantId/users/:id - ユーザー削除
 * 204 No Contentを返す
 */
function handleDeleteUser(
  userId: string,
): Observable<HttpResponse<null | { message: string }>> {
  const idx = MOCK_USER_DETAILS.findIndex((u) => u.publicId === userId);
  if (idx === -1) {
    return createMockErrorResponse('ユーザーが見つかりません', 404);
  }
  MOCK_USER_DETAILS.splice(idx, 1);
  return createMockResponse<null>(null, 204);
}

/**
 * POST /api/v0/tenants/:tenantId/users/:id/policies - ポリシー割当
 */
function handleAssignPolicy(
  userId: string,
  body: AssignPolicyApiRequest,
): Observable<HttpResponse<UserPoliciesApiResponse | { message: string }>> {
  const user = findUserDetailById(userId);
  if (!user) {
    return createMockErrorResponse('ユーザーが見つかりません', 404);
  }

  // 既に割り当て済みの場合は冪等にレスポンスのみ返す
  const alreadyAssigned = user.policies.some(
    (p) => p.publicId === body.policyId,
  );
  if (!alreadyAssigned) {
    const policyDetail = findPolicyDetailById(body.policyId);
    user.policies.push({
      publicId: body.policyId,
      name: policyDetail?.name ?? 'ポリシー',
      isTenantAdmin: policyDetail?.isTenantAdmin ?? false,
      permissions: policyDetail?.permissions ?? [],
    });
  }

  return createMockResponse<UserPoliciesApiResponse>({
    publicId: user.publicId,
    policies: user.policies.map((p) => ({
      publicId: p.publicId,
      name: p.name,
    })),
  });
}

/**
 * DELETE /api/v0/tenants/:tenantId/users/:id/policies/:policyId - ポリシー解除
 * 204 No Contentを返す
 */
function handleUnassignPolicy(
  userId: string,
  policyId: string,
): Observable<HttpResponse<null | { message: string }>> {
  const user = findUserDetailById(userId);
  if (!user) {
    return createMockErrorResponse('ユーザーが見つかりません', 404);
  }
  user.policies = user.policies.filter((p) => p.publicId !== policyId);
  return createMockResponse<null>(null, 204);
}
